/**
 * Search Decision Engine
 * Core decision logic for determining search presentation
 */

import { state } from '../core/state.js';
import { elements } from '../core/elements.js';
import { SEARCH_COPY, fillCopy, fillCopyWithSports } from './copy-templates.js';
import { buildLocationPlan, buildLabelTemplate, buildCenter, buildStateFilterFromNames } from './plan-builder.js';
import { readUserGeoFromCache } from './user-geo.js';
import { isZipCode, isLocationEffectivelySame, hasSpellingMistakes } from './location-comparison.js';
import { dateYmdToInt, getCampStartDate } from '../utils/date-utils.js';
import { normalizeCompareStr } from '../utils/string-utils.js';
import { updateFacets } from '../core/state.js';
import { getIndex } from '../algolia/client.js';
import { buildFilters } from '../filters/filter-builder.js';
import { renderResults } from '../ui/results-renderer.js';
import { updateURL } from '../url/url-updater.js';
import { updateFilterUI, updateFiltersCount } from '../ui/filter-ui.js';
import { toggleSkeleton } from '../ui/skeleton.js';

/**
 * @typedef {Object} SearchDecisionInput
 * @property {string} [rawLocationInput]
 * @property {boolean} hasDates
 * @property {Object|null} [geocode]
 * @property {Object|null} [userGeo]
 */

/**
 * @typedef {Object} SearchPlanStep
 * @property {string} key
 * @property {"MODE_A"|"MODE_B"|"MODE_C"|"MODE_D"} mode
 * @property {boolean} useDistance
 * @property {boolean} applyDates
 * @property {boolean} relaxDates
 * @property {{lat:number,lng:number}|null} distanceCenter
 * @property {number|null} radiusMiles
 * @property {string|null} stateFilter
 * @property {string|null} locationLabel
 * @property {string} labelTemplate
 * @property {string|null} headerHtml
 * @property {string|null} subtextHtml
 */

/**
 * @typedef {Object} SearchDecisionOutput
 * @property {SearchPlanStep[]} searchPlan
 * @property {string|null} headerHtml
 * @property {string|null} subtextHtml
 * @property {boolean} showDistance
 * @property {{lat:number,lng:number}|null} distanceCenter
 * @property {string} labelText
 */

/**
 * Main decision engine: decide search plan and presentation
 * @param {SearchDecisionInput} input
 * @returns {SearchDecisionOutput}
 */
export function decideSearchPresentation(input) {
  const defaults = window.CampsUtils?.defaults || {};
  const userGeo = input.userGeo || readUserGeoFromCache();
  const hasLocation = !!(input.rawLocationInput || '').trim();
  const hasDates = !!input.hasDates;
  const geocode = input.geocode || null;
  // Don't show mismatch banner for ZIP codes - it's normal for ZIP to be converted to city name
  const isZip = isZipCode(input.rawLocationInput || '');
  // Check if input text differs from what Google API returned
  // Use smart comparison to avoid showing banner for effectively same locations (e.g., "New York City" vs "New York")
  // Do not treat as mismatch when input is part of result (e.g. "Pebble Beach" in "Del Monte Forest")
  const hasMismatch =
    !isZip &&
    geocode?.recognized &&
    geocode.correctedLocationLabel &&
    input.rawLocationInput &&
    !geocode.inputIsPartOfResult &&
    !isLocationEffectivelySame(
      input.rawLocationInput,
      geocode.correctedLocationLabel
    );
  // Check for spelling mistakes to determine which message to show
  const hasTypos = hasMismatch
    ? hasSpellingMistakes(
        input.rawLocationInput,
        geocode.correctedLocationLabel,
        geocode.correctedCity
      )
    : false;
  // Always show mismatch banner when text differs (regardless of isLikelySameLocation)
  const mismatch = hasMismatch;

  /** @type {SearchPlanStep[]} */
  let searchPlan = [];

  if (hasLocation && !hasDates) {
    console.log(`📍 [DECIDE SEARCH] Building location plan (no dates):`, {
      rawInput: input.rawLocationInput || '',
      geocodeRecognized: geocode?.recognized,
      correctedState: geocode?.correctedState,
      correctedRegion: geocode?.correctedRegion,
      correctedCity: geocode?.correctedCity,
    });
    searchPlan = buildLocationPlan({
      rawInput: input.rawLocationInput || '',
      geocode,
      hasDates: false,
      normalizedMismatch: mismatch,
      hasSpellingMistakes: hasTypos,
      userGeo,
    });
    console.log(
      `📍 [DECIDE SEARCH] Location plan created:`,
      searchPlan.map((s) => s.key)
    );
  } else if (!hasLocation && hasDates) {
    const radius = defaults.radius || 250;
    if (userGeo && userGeo.isUS !== false) {
      const center = buildCenter(userGeo.lat, userGeo.lng);
      searchPlan.push({
        key: 'dates-user',
        mode: 'MODE_A',
        useDistance: true,
        applyDates: true,
        relaxDates: false,
        distanceCenter: center,
        radiusMiles: radius,
        stateFilter: null,
        locationLabel:
          userGeo.city && userGeo.state
            ? `${userGeo.city}, ${userGeo.state}`
            : null,
        labelTemplate: buildLabelTemplate(userGeo.city, userGeo.state),
        headerHtml: null,
        subtextHtml: null,
      });
      searchPlan.push({
        key: 'dates-user-nearby',
        mode: 'MODE_A',
        useDistance: true,
        applyDates: false,
        relaxDates: true,
        distanceCenter: center,
        radiusMiles: radius,
        stateFilter: null,
        locationLabel:
          userGeo.city && userGeo.state
            ? `${userGeo.city}, ${userGeo.state}`
            : null,
        labelTemplate: buildLabelTemplate(userGeo.city, userGeo.state),
        headerHtml: fillCopyWithSports(SEARCH_COPY.datesZeroHeader),
        subtextHtml: fillCopyWithSports(SEARCH_COPY.datesZeroSubtext),
      });
    } else if (userGeo && userGeo.isUS === false) {
      searchPlan.push({
        key: 'dates-nonus',
        mode: 'MODE_B',
        useDistance: false,
        applyDates: true,
        relaxDates: false,
        distanceCenter: null,
        radiusMiles: null,
        stateFilter: userGeo.state || null,
        locationLabel: userGeo.state || null,
        labelTemplate: buildLabelTemplate(null, userGeo.state),
        headerHtml: null,
        subtextHtml: null,
      });
      searchPlan.push({
        key: 'dates-nonus-nearby',
        mode: 'MODE_B',
        useDistance: false,
        applyDates: false,
        relaxDates: true,
        distanceCenter: null,
        radiusMiles: null,
        stateFilter: userGeo.state || null,
        locationLabel: userGeo.state || null,
        labelTemplate: buildLabelTemplate(null, userGeo.state),
        headerHtml: fillCopyWithSports(SEARCH_COPY.datesZeroHeader),
        subtextHtml: fillCopyWithSports(SEARCH_COPY.datesZeroSubtext),
      });
    } else {
      searchPlan.push({
        key: 'dates-global',
        mode: 'MODE_D',
        useDistance: false,
        applyDates: true,
        relaxDates: false,
        distanceCenter: null,
        radiusMiles: null,
        stateFilter: null,
        locationLabel: null,
        labelTemplate: buildLabelTemplate(null, null),
        headerHtml: null,
        subtextHtml: null,
      });
      searchPlan.push({
        key: 'dates-global-nearby',
        mode: 'MODE_D',
        useDistance: false,
        applyDates: false,
        relaxDates: true,
        distanceCenter: null,
        radiusMiles: null,
        stateFilter: null,
        locationLabel: null,
        labelTemplate: buildLabelTemplate(null, null),
        headerHtml: fillCopyWithSports(SEARCH_COPY.datesZeroHeader),
        subtextHtml: fillCopyWithSports(SEARCH_COPY.datesZeroSubtext),
      });
    }
  } else if (hasLocation && hasDates) {
    const locationPlan = buildLocationPlan({
      rawInput: input.rawLocationInput || '',
      geocode,
      hasDates: true,
      normalizedMismatch: mismatch,
      hasSpellingMistakes: hasTypos,
      useDatesHeader: true,
      userGeo,
    });

    // Add "remove dates" fallback
    if (geocode?.recognized) {
      const center = buildCenter(geocode.correctedLat, geocode.correctedLng);
      const isZip = isZipCode(input.rawLocationInput || '');
      const displayLocationLabel = isZip
        ? input.rawLocationInput || ''
        : geocode.correctedLocationLabel || '';
      const isNonUS = userGeo?.isUS === false;
      const stateName = geocode.correctedState || geocode.correctedRegion;
      const isStateInputDec = !geocode.correctedCity || geocode.correctedCity.trim() === '';
      let userMatchesStateLocNo = false;

      if (stateName) {
        const fullStateName =
          geocode.correctedRegion || geocode.correctedState;
        const map = window.CampsUtils?.mapStateNameToCode;
        const normalizedCode =
          geocode.correctedState || map?.(geocode.correctedRegion) || geocode.correctedRegion;
        const stateFilterStr = buildStateFilterFromNames(normalizedCode, fullStateName);
        const userStateNorm = (userGeo?.state && (map?.(userGeo.state) || userGeo.state)) || '';
        const userMatchesState =
          !!(
            normalizedCode &&
            userStateNorm &&
            (map?.(userStateNorm) || userStateNorm) === normalizedCode &&
            userGeo?.lat != null &&
            userGeo?.lng != null
          );
        userMatchesStateLocNo = userMatchesState;
        console.log(
          `📍 [DECISION FLOW] Adding state fallback step: ${fullStateName}, userMatchesState=${userMatchesState}`
        );
        locationPlan.push({
          key: 'state',
          mode: 'MODE_A',
          useDistance: isNonUS ? false : userMatchesState,
          applyDates: false,
          relaxDates: true,
          distanceCenter: userMatchesState ? { lat: userGeo.lat, lng: userGeo.lng } : null,
          radiusMiles: null,
          stateFilter: stateFilterStr,
          locationLabel: fullStateName,
          labelTemplate: buildLabelTemplate(null, fullStateName),
          headerHtml: isStateInputDec
            ? null
            : fillCopyWithSports(SEARCH_COPY.datesLocationZeroHeader, {
                locationLabel: displayLocationLabel,
              }),
          subtextHtml: isStateInputDec
            ? null
            : fillCopyWithSports(SEARCH_COPY.datesLocationStateSubtext, {
                stateCode: fullStateName,
              }),
        });
      } else {
        console.warn(
          '⚠️ [DECISION FLOW] No state name found for state fallback',
          {
            correctedState: geocode.correctedState,
            correctedRegion: geocode.correctedRegion,
          }
        );
      }

      // Location fallback without dates. When correction is state-level: no radius, same as state step.
      // For recognized locations, always apply radius (standard behavior)
      const isStateLevelLocNo = isStateInputDec && !!stateName;
      locationPlan.push({
        key: 'loc-no-dates',
        mode: 'MODE_A',
        useDistance: isNonUS ? false : (isStateLevelLocNo ? userMatchesStateLocNo : true),
        applyDates: false,
        relaxDates: true,
        distanceCenter: isStateLevelLocNo
          ? (userMatchesStateLocNo ? { lat: userGeo.lat, lng: userGeo.lng } : null)
          : center,
        radiusMiles: isStateLevelLocNo ? null : (defaults.radius || 250),
        stateFilter: null,
        locationLabel: displayLocationLabel,
        labelTemplate: buildLabelTemplate(
          geocode.correctedCity,
          geocode.correctedState,
          input.rawLocationInput || ''
        ),
        headerHtml: fillCopyWithSports(SEARCH_COPY.datesLocationZeroHeader, {
          locationLabel: displayLocationLabel,
        }),
        subtextHtml: fillCopyWithSports(
          SEARCH_COPY.datesLocationZeroSubtext,
          { locationLabel: displayLocationLabel }
        ),
      });
    }

    // Append state/global fallbacks already contained inside locationPlan
    searchPlan = locationPlan;

    // If everything fails, final global fallback
    // Use rawInput (ZIP code) if input was ZIP, otherwise use correctedLocationLabel
    const isZip = isZipCode(input.rawLocationInput || '');
    const displayLocationLabel = isZip
      ? input.rawLocationInput || ''
      : geocode?.correctedLocationLabel || '';
    // Set center based on user location or fallback for sorting in Algolia
    const globalCenter =
      buildCenter(userGeo?.lat, userGeo?.lng) ||
      buildCenter(defaults.fallbackCoords?.lat, defaults.fallbackCoords?.lng);
    const isNonUS = userGeo?.isUS === false;
    searchPlan.push({
      key: 'loc-global',
      mode: 'MODE_D',
      useDistance: false,
      applyDates: false,
      relaxDates: true,
      distanceCenter: globalCenter, // Use user location or fallback for Algolia sorting
      radiusMiles: null,
      stateFilter: null,
      locationLabel: null,
      labelTemplate: buildLabelTemplate(null, null),
      headerHtml: fillCopyWithSports(SEARCH_COPY.datesLocationZeroHeader, {
        locationLabel: displayLocationLabel,
      }),
      subtextHtml: fillCopy(SEARCH_COPY.datesLocationGlobalSubtext, {}),
    });
  } else {
    // No location, no dates: default to user geo or fallback center with distance, then global
    const center =
      buildCenter(userGeo?.lat, userGeo?.lng) ||
      buildCenter(defaults.fallbackCoords?.lat, defaults.fallbackCoords?.lng);
    const labelTemplate = buildLabelTemplate(
      userGeo?.city,
      userGeo?.state || defaults.fallbackCoords?.regionCode
    );
    // For non-US users, hide distance even without dates
    const isNonUS = userGeo?.isUS === false;
    searchPlan.push({
      key: 'default-geo',
      mode: 'MODE_A',
      useDistance: isNonUS ? false : !!center,
      applyDates: false,
      relaxDates: false,
      distanceCenter: center,
      radiusMiles: isNonUS ? null : (defaults.radius || 250),
      stateFilter: null,
      locationLabel:
        (userGeo?.city &&
          userGeo?.state &&
          `${userGeo.city}, ${userGeo.state}`) ||
        (defaults.fallbackCoords?.city &&
          defaults.fallbackCoords?.regionCode &&
          `${defaults.fallbackCoords.city}, ${defaults.fallbackCoords.regionCode}`) ||
        null,
      labelTemplate: labelTemplate || buildLabelTemplate(null, null),
      headerHtml: null,
      subtextHtml: null,
    });
    searchPlan.push({
      key: 'default-global',
      mode: 'MODE_D',
      useDistance: false,
      applyDates: false,
      relaxDates: false,
      distanceCenter: null,
      radiusMiles: null,
      stateFilter: null,
      locationLabel: null,
      labelTemplate: buildLabelTemplate(null, null),
      headerHtml: null,
      subtextHtml: null,
    });
  }

  const initialPresentation = searchPlan[0] || {
    labelTemplate: buildLabelTemplate(null, null),
    headerHtml: null,
    subtextHtml: null,
    useDistance: false,
    distanceCenter: null,
  };

  return {
    searchPlan,
    headerHtml: initialPresentation.headerHtml,
    subtextHtml: initialPresentation.subtextHtml,
    showDistance: initialPresentation.useDistance,
    distanceCenter: initialPresentation.distanceCenter,
    labelText: initialPresentation.labelTemplate,
  };
}

/**
 * Build Algolia filters string for a specific step.
 * For state/state-dates steps: state filtering via address.state, not geo.
 * @param {SearchPlanStep} step
 * @param {Function} buildFilters - Import from filters/filter-builder.js
 * @returns {string|undefined}
 */
export function buildStepFilters(step, buildFilters) {
  const isStateStep = step?.key === 'state' || step?.key === 'state-dates';
  if (isStateStep && step.stateFilter) {
    const base = buildFilters(true);
    return base ? `${base} AND ${step.stateFilter}` : step.stateFilter;
  }
  // "loc-global" is a global fallback — skip state filter even if state.isStateSearch is true
  if (step?.key === 'loc-global') {
    return buildFilters(true);
  }
  return buildFilters();
}

/**
 * Compute showDistance: non-US rules, state-search (user state match), or step value.
 * @param {boolean} stepUseDistance
 * @param {SearchPlanStep|null} step - pass step for state steps (use step value as-is)
 * @returns {boolean}
 */
export function computeShowDistance(stepUseDistance, step) {
  // England/Scotland/UK: use coords for sorting but don't show distance
  if (state.sortByDistanceNoRadius) return false;
  // Individual UK country search: no distance display (no coords available)
  if (state.ukCountryFilter) return false;

  const userGeo = readUserGeoFromCache();
  const hasDates = !!(state.beginDates && state.endDates);
  const hasLocation = !!(state.zip || '').trim();
  const isNonUS = userGeo?.isUS === false;
  const shouldHideDistance = isNonUS && (hasDates || !hasLocation);
  if (shouldHideDistance) return false;
  const isStateStep = step?.key === 'state' || step?.key === 'state-dates';
  if (isStateStep) return !!stepUseDistance;
  if (state.isStateSearch) {
    const map = window.CampsUtils?.mapStateNameToCode;
    const userCode = (userGeo?.state && (map?.(userGeo.state) || userGeo.state)) || '';
    return !!(state.searchStateCode && userCode && (map?.(userCode) || userCode) === state.searchStateCode);
  }
  return !!stepUseDistance;
}

/**
 * Apply presentation (header/subtext/labels) to DOM.
 * @param {SearchPlanStep} step
 * @param {number} count
 */
export function applyPresentation(step, count) {
  state.showDistance = computeShowDistance(step.useDistance, step);

  // Update results heading with label from step
  if (step.labelTemplate && elements.resultsHeading) {
    const label = step.labelTemplate.replace('{count}', count || 0);
    elements.resultsHeading.textContent = label;
  }

  // Update results count
  if (elements.resultsCount) {
    const label = step.labelTemplate
      ? step.labelTemplate.replace('{count}', count || 0)
      : `(${count || 0} camps)`;
    // Extract count part for results-count (format: "(X camps)")
    const countMatch = label.match(/\((\d+)\s+camps\)/);
    if (countMatch) {
      elements.resultsCount.textContent = countMatch[0];
    } else {
      elements.resultsCount.textContent = `(${count || 0} camps)`;
    }
  }

  const hasCopy = step.headerHtml || step.subtextHtml;
  if (elements.fallbackBanner) {
    elements.fallbackBanner.style.display = hasCopy ? 'block' : 'none';
  }
  if (elements.fallbackTitle) {
    elements.fallbackTitle.innerHTML = step.headerHtml || '';
  }
  if (elements.fallbackText) {
    elements.fallbackText.innerHTML = step.subtextHtml || '';
  }
}

/**
 * Hide fallback banner
 */
export function hideFallbackBanner() {
  if (elements.fallbackBanner) elements.fallbackBanner.style.display = 'none';
  if (elements.fallbackTitle) elements.fallbackTitle.innerHTML = '';
  if (elements.fallbackText) elements.fallbackText.innerHTML = '';
}

/**
 * Show corrected location banner if needed
 * @param {number} count
 */
export function showCorrectedLocationBannerIfNeeded(count = 0) {
  if (!state.showCorrectedLocationBanner) return;
  if (!state.geocodeResult?.recognized) return;

  // Don't show "couldn't find camps" message if camps were actually found
  // This message should only appear when there are no results or it's a fallback
  if (count > 0) {
    // Hide banner if camps were found - no need to show misleading "couldn't find" message
    hideFallbackBanner();
    return;
  }

  const rawInput = state.geocodeResult.rawInput || state.zip || '';
  const correctedLabel = state.geocodeResult.correctedLocationLabel || '';
  const correctedCity = state.geocodeResult.correctedCity || '';

  // First, check if city name matches - if yes, don't show "unrecognized" banner
  // This handles cases like "California, MD" where user input matches the city name
  const cityMatches =
    correctedCity && rawInput
      ? isLocationEffectivelySame(rawInput, correctedCity) ||
        normalizeCompareStr(rawInput).includes(
          normalizeCompareStr(correctedCity)
        ) ||
        normalizeCompareStr(correctedCity).includes(
          normalizeCompareStr(rawInput)
        )
      : false;

  // Only check for typos if city doesn't match
  const hasTypos =
    !cityMatches &&
    hasSpellingMistakes(rawInput, correctedLabel, correctedCity);

  // Use "unrecognized" message for spelling mistakes, "normalizedMismatch" for other mismatches
  // But if city matches, use "normalizedMismatch" instead of "unrecognized"
  const headerHtml =
    hasTypos && !cityMatches
      ? fillCopy(SEARCH_COPY.unrecognizedHeader, { rawInput })
      : fillCopy(SEARCH_COPY.normalizedMismatchHeader, { rawInput });
  const subtextHtml = hasTypos
    ? fillCopyWithSports(SEARCH_COPY.unrecognizedSubtextUser, {
        locationLabel: correctedLabel,
      })
    : fillCopyWithSports(SEARCH_COPY.normalizedMismatchSubtext, {
        locationLabel: correctedLabel,
      });

  applyPresentation(
    {
      useDistance: true,
      headerHtml,
      subtextHtml,
      labelTemplate: '',
    },
    count
  );
}

/**
 * Execute a single search step.
 * @param {SearchPlanStep} step
 * @param {Object} index - Algolia index
 * @param {Function} buildFilters - Filter builder function
 * @returns {Promise<{hits:any[],nbHits:number,facets:Object}>}
 */
export async function runSearchStep(step, index, buildFilters) {
  console.log('🔍 [DECISION FLOW] Running search step:', step.key, {
    mode: step.mode,
    stateFilter: step.stateFilter,
    useDistance: step.useDistance,
    radiusMiles: step.radiusMiles,
  });
  const filters = buildStepFilters(step, buildFilters) || undefined;
  console.log('🔍 [DECISION FLOW] Filters:', filters);
  const geoParams = {};
  const isStateStep = step.key === 'state' || step.key === 'state-dates';
  // State steps: no radius (filter by address.state). aroundLatLng only when user's state matches (for sorting/distance).
  if (step.distanceCenter) {
    geoParams.aroundLatLng = `${step.distanceCenter.lat},${step.distanceCenter.lng}`;
    if (!isStateStep && step.useDistance && step.radiusMiles) {
      geoParams.aroundRadius = step.radiusMiles * 1609;
    }
    console.log('📍 [DECISION FLOW] Using distanceCenter:', {
      lat: step.distanceCenter.lat,
      lng: step.distanceCenter.lng,
      aroundRadius: geoParams.aroundRadius,
    });
  } else if (!isStateStep) {
    console.warn('⚠️ [DECISION FLOW] No distanceCenter for step:', step.key);
  }
  console.log('🔍 [DECISION FLOW] Geo params:', geoParams);

  // Standard single query - get up to 1000 results, but use nbHits for total count
  // Don't use numericFilters - we'll filter on client to include camps without dates
  const searchParams = {
    filters,
    hitsPerPage: 1000, // Get up to 1000 results per query
    page: 0,
    facets: ['sportSlug', 'feeCodes', 'gender', 'brands', 'skills', 'live'],
    // Don't use numericFilters - we'll filter on client to include camps without dates
    getRankingInfo: true,
    ...geoParams,
  };
  // Sorting flags: keep client-side as before
  if (state.sort === 'title-asc') {
    searchParams.sortFacetValuesBy = 'alpha';
  }
  return index.search('', searchParams);
}

/**
 * Execute decision-based search with fallbacks
 * Zero-arg: resolves the Algolia index and UI/filter dependencies via module
 * imports (all call sites invoke it without arguments).
 * @returns {Promise<void>}
 */
export async function runDecisionFlow() {
  const index = getIndex();
  // Reset dual search state - this is a new search, not filter change
  state.foundCampObjectID = null;

  const decisionInput = {
    rawLocationInput: state.zip || '',
    hasDates: !!(state.beginDates && state.endDates),
    geocode: state.geocodeResult || null,
    userGeo: readUserGeoFromCache(),
  };

  const decision = decideSearchPresentation(decisionInput);
  console.log(
    '🔍 [DECISION FLOW] Search plan:',
    decision.searchPlan.map((s) => ({
      key: s.key,
      applyDates: s.applyDates,
      locationLabel: s.locationLabel,
    }))
  );

  for (const step of decision.searchPlan) {
    try {
      // State steps use address.state filter and optional user coords for sorting; no geocoding.

      const { hits, nbHits, facets } = await runSearchStep(step, index, buildFilters);

      // Keep facets from last attempt for UI stability
      if (facets) {
        updateFacets(facets || {});
      }

      // Apply client-side date filtering using sessionStartDays (YYYYMMDD integers)
      // Camps without sessionStartDays are always included (no date data)
      let filteredHits = hits || [];
      if (step.applyDates && state.beginDates && state.endDates) {
        const startDay = dateYmdToInt(state.beginDates);
        const endDay = dateYmdToInt(state.endDates);
        if (startDay && endDay) {
          filteredHits = filteredHits.filter((camp) => {
            // Include camps without sessionStartDays (no date info)
            if (!camp.sessionStartDays?.length) return true;
            // Include camps where any session starts within the selected range
            return camp.sessionStartDays.some((d) => d >= startDay && d <= endDay);
          });
        }
      }

      // Check results after date filtering - if step applies dates and results are 0, skip to next step
      const hasResults = filteredHits.length > 0;
      console.log(
        `🔍 [DECISION FLOW] Step ${step.key}: nbHits=${nbHits}, hits.length=${hits.length}, filteredHits.length=${filteredHits.length}, hasResults=${hasResults}`
      );

      if (!hasResults) {
        console.log(
          `⏭️ [DECISION FLOW] Skipping step ${step.key} - no results after filtering, continuing to next step`
        );
        continue;
      }

      // Sync state.coordinates with step's center so performSearch (e.g. on filter change) uses the same center.
      // EXCEPT "loc-global": its center is the user's IP (or nothing) used only for
      // sorting the global fallback — syncing it would pollute state.coordinates, so
      // the next filter toggle would re-center on the IP instead of the searched city
      // (e.g. Texas City) and show the wrong nearby camps.
      if (step.distanceCenter && step.key !== 'loc-global') {
        state.coordinates = {
          lat: step.distanceCenter.lat,
          lng: step.distanceCenter.lng,
        };
      }

      // Show corrected location banner if there are spelling mistakes
      // This should be shown before step presentation, but only if we have results
      if (state.showCorrectedLocationBanner) {
        showCorrectedLocationBannerIfNeeded(filteredHits.length);
      } else {
        // Use step presentation if no corrected location banner needed
        applyPresentation(step, filteredHits.length);
      }

      // Store banner content for unrecognized location so we can restore it when filtering
      if (
        state.showUnrecognizedLocationBanner &&
        (step.headerHtml || step.subtextHtml)
      ) {
        state.unrecognizedLocationBannerContent = {
          headerHtml: step.headerHtml,
          subtextHtml: step.subtextHtml,
          useDistance: step.useDistance || false,
        };
      }

      // Sort client-side if needed (reuse previous logic)
      if (state.sort === 'title-desc') {
        filteredHits.sort((a, b) =>
          (b.title || '')
            .toLowerCase()
            .localeCompare((a.title || '').toLowerCase())
        );
      } else if (state.sort === 'title-asc') {
        filteredHits.sort((a, b) =>
          (a.title || '')
            .toLowerCase()
            .localeCompare((b.title || '').toLowerCase())
        );
      } else if (state.sort === 'date-asc') {
        filteredHits.sort((a, b) => {
          const dateA = getCampStartDate(a);
          const dateB = getCampStartDate(b);
          if (!dateA && !dateB) {
            return (a.title || '')
              .toLowerCase()
              .localeCompare((b.title || '').toLowerCase());
          }
          if (!dateA) return 1;
          if (!dateB) return -1;
          const diff = dateA - dateB;
          if (diff === 0) {
            return (a.title || '')
              .toLowerCase()
              .localeCompare((b.title || '').toLowerCase());
          }
          return diff;
        });
      }

      // Featured camps A/B test: pin featured camps to the top (variant B; no-op otherwise).
      // Gated on the active sport filter — city-only (no sport) does not pin.
      filteredHits =
        window.CampsUtils?.applyFeaturedBoost?.(filteredHits, state.sportSlug) ||
        filteredHits;

      state.allFilteredHits = filteredHits;
      // Use filteredHits.length for total count after date filtering
      // If dates were filtered, use filtered count; otherwise use nbHits
      const totalCount =
        step.applyDates && state.beginDates && state.endDates
          ? filteredHits.length
          : nbHits || 0;
      state.totalHits = totalCount;
      state.displayedHits = state.hitsPerPage;

      const hitsToDisplay = filteredHits.slice(0, state.displayedHits);
      // Pass step.labelTemplate to renderResults so it uses correct heading (e.g., "California: X camps found" instead of "San Jose, CA: X camps found")
      // Use filtered count if dates were filtered, otherwise use nbHits
      renderResults(hitsToDisplay, totalCount, false, step.labelTemplate);
      updateURL();
      updateFilterUI();
      // Keep the "Filters (N)" counter honest on fallback renders that bypass
      // performSearch (e.g. unrecognized location) — live counts as 1 when on
      updateFiltersCount();
      toggleSkeleton(false);
      return;
    } catch (e) {
      console.warn('Decision flow step failed', step.key, e);
      continue;
    }
  }

  // No results across all fallbacks
  const lastStep = decision.searchPlan[decision.searchPlan.length - 1] || {
    labelTemplate: '{count} camps found',
    headerHtml: fillCopyWithSports(SEARCH_COPY.datesZeroHeader),
    subtextHtml: fillCopyWithSports(SEARCH_COPY.datesZeroSubtext),
    useDistance: false,
    distanceCenter: null,
  };
  applyPresentation(lastStep, 0);
  renderResults([], 0, false, lastStep.labelTemplate);
  updateURL();
  updateFilterUI();
  updateFiltersCount();
  toggleSkeleton(false);
}

console.log('📦 Decision engine module loaded');
